import { TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN } from "./productAliases";
import {
  isProductBlockClose,
  parseProductBlockOpen,
  PRODUCT_BLOCK_CLOSE_MARKER,
  productBlockOpenMarker,
} from "./productBlocks";
import { maxProductItemIndex } from "./productItemTags";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

import { hasProductContextTags } from "@/services/mergeTags";

/**
 * A product context is a section bound to one product model. It holds ordinary
 * copy and product blocks, and the whole section drops when the model has no
 * items — so a "Your cart" headline can't outlive an empty cart.
 *
 * In `bodyText` it survives as the email dialect's `{% with %}` wrapper — the
 * same shape the email editor exports — so a cart section pasted from an email
 * round-trips as-is:
 *
 *     {% with items=tile_data.cart_items %}
 *     Still in your cart:
 *     {% if items.0 %}
 *     {{ items.0.name }} — {{ items.0.price }}
 *     {% endif %}
 *     {% endwith %}
 *
 * The model is named in the `tile_data.<model>` binding because the context is
 * where it's authored, not derived from the tile. Indices are absolute and
 * honest: a second context on the same model continues where the first stopped
 * (`items.2`, `items.3`, …) — the authoring state is the wire DTL.
 */
const CONTEXT_OPEN_PATTERN = /^\{%\s*with\s+items=tile_data\.([\w]+)\s*%\}$/;
// Discover binds per grid — `tile_data.dynamic.<g>.items`, indices restarting
// at 0 in each grid. The optional `heading=` binding is what the email editor
// emits; tolerated on parse, dropped on re-serialize (SMS has no heading).
const DISCOVER_OPEN_PATTERN =
  /^\{%\s*with\s+items=tile_data\.dynamic\.(\d+)\.items(?:\s*,\s*heading=tile_data\.dynamic\.\d+\.header)?\s*%\}$/;
const CONTEXT_CLOSE_PATTERN = /^\{%\s*endwith\s*%\}$/;

export const PRODUCT_CONTEXT_CLOSE_MARKER = "{% endwith %}";

/** Discover sends one authored set plus mirrors: `dynamic.0` … `dynamic.4`. */
export const DISCOVER_GRID_COUNT = 5;
/** The per-set header the discover open marker binds; `{{heading}}` reads it. */
export const DISCOVER_HEADING_TAG = "heading";
const HEADING_TAG_PATTERN = /\{\{\s*heading\s*(?:\|[^}]*)?\}\}/;

/** A line carrying the Discover headline tag — it belongs to the set, like a block. */
export function isDiscoverHeadingLine(line: string): boolean {
  return HEADING_TAG_PATTERN.test(line);
}

export interface ProductContextOpen {
  model: string;
  /** Discover grid ordinal; null for single-binding models. */
  grid: number | null;
}

export function productContextOpenMarker(
  model: string,
  grid?: number | null,
): string {
  return model === "discover"
    ? `{% with items=tile_data.dynamic.${grid ?? 0}.items, heading=tile_data.dynamic.${grid ?? 0}.header %}`
    : `{% with items=tile_data.${model} %}`;
}

export function parseProductContextOpen(
  line: string,
): ProductContextOpen | null {
  const trimmed = line.trim();
  const discover = DISCOVER_OPEN_PATTERN.exec(trimmed);
  if (discover) {
    return { model: "discover", grid: Number.parseInt(discover[1], 10) };
  }
  const match = CONTEXT_OPEN_PATTERN.exec(trimmed);
  return match ? { model: match[1], grid: null } : null;
}

export function isProductContextClose(line: string): boolean {
  return CONTEXT_CLOSE_PATTERN.test(line.trim());
}

/** True when any line of `text` opens a product context. */
export function hasProductContextMarkers(text: string): boolean {
  return text
    .split("\n")
    .some((line) => parseProductContextOpen(line) !== null);
}

interface TextSegment {
  kind: "text";
  lines: string[];
}

interface BlockSegment {
  kind: "block";
  index: number;
  lines: string[];
}

interface ContextSegment {
  kind: "context";
  model: string;
  /** Discover grid ordinal; null for single-binding models. */
  grid: number | null;
  children: Array<TextSegment | BlockSegment>;
}

export type ProductSegment = TextSegment | BlockSegment | ContextSegment;

/**
 * Splits body text into contexts, product blocks and ordinary copy.
 *
 * Unterminated markers close at the end rather than dropping their content —
 * hand-edited or truncated text still round-trips everything the author wrote.
 */
export function parseProductSegments(text: string): ProductSegment[] {
  const root: ProductSegment[] = [];
  let context: ContextSegment | null = null;
  let block: BlockSegment | null = null;
  let run: TextSegment | null = null;

  const push = (segment: TextSegment | BlockSegment) => {
    if (context) context.children.push(segment);
    else root.push(segment);
  };

  for (const line of text.split("\n")) {
    const open = parseProductContextOpen(line);
    if (open !== null) {
      block = null;
      run = null;
      context = {
        kind: "context",
        model: open.model,
        grid: open.grid,
        children: [],
      };
      root.push(context);
      continue;
    }
    if (isProductContextClose(line)) {
      context = null;
      block = null;
      run = null;
      continue;
    }

    const index = parseProductBlockOpen(line);
    if (index !== null) {
      run = null;
      block = { kind: "block", index, lines: [] };
      push(block);
      continue;
    }
    if (isProductBlockClose(line)) {
      block = null;
      run = null;
      continue;
    }

    if (block) {
      block.lines.push(line);
      continue;
    }
    if (!run) {
      run = { kind: "text", lines: [] };
      push(run);
    }
    run.lines.push(line);
  }

  const prune = (segments: ProductSegment[]): ProductSegment[] =>
    segments.filter((segment) =>
      segment.kind === "context"
        ? (segment.children = prune(segment.children) as Array<
            TextSegment | BlockSegment
          >).length > 0
        : segment.lines.length > 0,
    );

  return prune(root);
}

/** True for a copy run whose every line carries the Discover headline. */
function isHeadingOnly(segment: TextSegment): boolean {
  return segment.lines.every(isDiscoverHeadingLine);
}

/**
 * The author writes one Discover section; the other sets repeat its product
 * blocks and headline lines against their own `dynamic.<g>` binding. Other
 * copy sends once. Only a lone discover context mirrors — hand-authored
 * multi-grid text sends as written.
 */
export function expandDiscoverMirrors(
  segments: ProductSegment[],
): ProductSegment[] {
  const discover = segments.filter(
    (segment): segment is ContextSegment =>
      segment.kind === "context" && segment.model === "discover",
  );
  if (discover.length !== 1) return segments;
  const [source] = discover;
  const children: Array<TextSegment | BlockSegment> = [];
  for (const child of source.children) {
    if (child.kind === "block") {
      children.push(child);
      continue;
    }
    const lines = child.lines.filter(isDiscoverHeadingLine);
    if (lines.length > 0) children.push({ kind: "text", lines });
  }
  if (!children.some((child) => child.kind === "block")) return segments;
  const mirrors: ContextSegment[] = [];
  for (let grid = 0; grid < DISCOVER_GRID_COUNT; grid += 1) {
    if (grid === source.grid) continue;
    // Own array per set, so a caller mutating one mirror can't alias the rest.
    mirrors.push({
      kind: "context",
      model: "discover",
      grid,
      children: [...children],
    });
  }
  const at = segments.indexOf(source) + 1;
  return [...segments.slice(0, at), ...mirrors, ...segments.slice(at)];
}

interface Rendered {
  guarded: boolean;
  open: string;
  close: string;
  body: string;
}

/**
 * Joins rendered segments, folding the line separator *inside* the guard so a
 * dropped segment leaves no blank line. A guarded segment that leads takes the
 * separator that follows it instead, having none in front.
 */
function fold(items: Rendered[]): string {
  const parts: string[] = [];
  let skipLeading = false;

  items.forEach((item, index) => {
    const leading = index > 0 && !skipLeading ? "\n" : "";
    skipLeading = false;

    if (!item.guarded) {
      parts.push(`${leading}${item.body}`);
      return;
    }
    if (index === 0 && items.length > 1) {
      parts.push(`${item.open}${item.body}\n${item.close}`);
      skipLeading = true;
      return;
    }
    parts.push(`${item.open}${leading}${item.body}${item.close}`);
  });

  return parts.join("");
}

function renderInline(segment: ProductSegment): Rendered {
  if (segment.kind === "text") {
    return {
      guarded: false,
      open: "",
      close: "",
      body: segment.lines.join("\n"),
    };
  }
  if (segment.kind === "block") {
    return {
      guarded: true,
      open: productBlockOpenMarker(segment.index),
      close: PRODUCT_BLOCK_CLOSE_MARKER,
      body: segment.lines.join("\n"),
    };
  }
  // A blocks-only context gets an outer guard on its first item — the email's
  // section-guard shape — so an empty model folds the whole section away,
  // separator included. A context holding loose copy stays unguarded: that
  // copy renders whether or not the model has items (the migrated-legacy
  // contract depends on it), so the wrapper travels in the body — fold only
  // honours open/close on guarded segments.
  const inner = fold(segment.children.map(renderInline));
  const openMarker = productContextOpenMarker(segment.model, segment.grid);
  const blocks = segment.children.filter(
    (child): child is BlockSegment => child.kind === "block",
  );
  // A heading line drops with the products it introduces, like a block.
  const guardable = segment.children.every(
    (child) => child.kind === "block" || isHeadingOnly(child),
  );
  if (blocks.length === 0 || !guardable) {
    return {
      guarded: false,
      open: "",
      close: "",
      body: `${openMarker}${inner}${PRODUCT_CONTEXT_CLOSE_MARKER}`,
    };
  }
  return {
    guarded: true,
    open: `${openMarker}${productBlockOpenMarker(blocks[0]?.index ?? 0)}`,
    close: `${PRODUCT_BLOCK_CLOSE_MARKER}${PRODUCT_CONTEXT_CLOSE_MARKER}`,
    body: inner,
  };
}

/**
 * Rewrites the authoring state's whole-line markers into inline DTL for the
 * wire. The markers occupy their own lines so the editor can round-trip them
 * with a sibling scan, but rendered as-is those lines become empty strings and
 * leave their `\n` behind — so this pass inlines the markers and folds each
 * separator inside its guard. Marker-free text passes through by reference.
 */
export function inlineProductMarkers(text: string): string {
  const hasMarkers = text
    .split("\n")
    .some(
      (line) =>
        parseProductContextOpen(line) !== null ||
        parseProductBlockOpen(line) !== null,
    );
  if (!hasMarkers) return text;
  return fold(
    expandDiscoverMirrors(parseProductSegments(text)).map(renderInline),
  );
}

/** Highest `items.N` reference in a context: block markers and loose tags. */
function maxItemIndexIn(context: ContextSegment): number {
  let max = -1;
  for (const child of context.children) {
    if (child.kind === "block") max = Math.max(max, child.index);
    max = Math.max(max, maxProductItemIndex(child.lines.join("\n")));
  }
  return max;
}

export interface ProductContextLayoutEntry {
  context: ContextSegment;
  /** First item index this context's grid fills (its `product_grids` offset). */
  base: number;
  /** Items this context asks its grid to source (`max_products` ceiling). */
  maxProducts: number;
}

/**
 * Contexts in document order with the item range each one claims. The base is
 * a per-model running sum — the same rule the backend applies when it sums
 * `max_products` per model — so the absolute indices authored in the text line
 * up with what actually gets fetched. A context with product references whose
 * indices sit below its base (hand-edited text) still claims at least one
 * item; an *empty* wrapper — no blocks, no product tags, only plain copy —
 * claims none and costs no fetch.
 */
export function productContextLayout(
  segments: ProductSegment[],
): ProductContextLayoutEntry[] {
  const running: Record<string, number> = {};
  const entries: ProductContextLayoutEntry[] = [];
  for (const segment of segments) {
    if (segment.kind !== "context") continue;
    // Discover binds one items array per grid — indices restart at 0 in each
    // context and nothing accumulates across them.
    const base =
      segment.model === "discover" ? 0 : (running[segment.model] ?? 0);
    const maxIndex = maxItemIndexIn(segment);
    const hasBlocks = segment.children.some((child) => child.kind === "block");
    const maxProducts =
      !hasBlocks && maxIndex < 0 ? 0 : Math.max(1, maxIndex - base + 1);
    if (segment.model !== "discover") {
      running[segment.model] = base + maxProducts;
    }
    entries.push({ context: segment, base, maxProducts });
  }
  return entries;
}

export interface ProductConsumerEntry {
  model: string;
  maxProducts: number;
}

/** Handwritten absolute paths: `tile_data.<model>[n]`, `tile_data.dynamic[g].items[n]`. */
const TILE_DATA_ITEM_INDEX_PATTERN =
  /tile_data\.\w+(?:\[\d+\]\.items)?\[(\d+)\]/g;

/** Highest product index claimed by copy outside every context, or -1. */
function maxLooseProductIndex(looseText: string): number {
  let max = -1;
  TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN.lastIndex = 0;
  if (TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN.test(looseText)) max = 0;
  if (hasProductContextTags(looseText)) max = Math.max(max, 0);
  for (const match of looseText.matchAll(TILE_DATA_ITEM_INDEX_PATTERN)) {
    max = Math.max(max, Number.parseInt(match[1], 10));
  }
  return max;
}

/**
 * One `product_grids` entry per product context, in document order — the text
 * sibling of the email editor's `extractProductConfig`.
 *
 * Copy outside every context contributes a trailing entry on the tile's derived
 * model, so legacy drafts (V1 aliases, handwritten `tile_data.*` tags) keep
 * their config. Keys are assigned by the caller and must be numeric strings;
 * the backend parses them as int32 and sums `max_products` per model, which is
 * what makes the absolute indices above line up with what actually gets
 * fetched.
 */
export function collectProductConsumers(
  text: string,
  defaultModel: ProductGridModelValue,
): ProductConsumerEntry[] {
  const segments = parseProductSegments(text);
  const entries: ProductConsumerEntry[] = productContextLayout(segments)
    // Empty wrappers claim no products — no grid request for them.
    .filter(({ maxProducts }) => maxProducts > 0)
    .map(({ context, maxProducts }) => ({ model: context.model, maxProducts }));

  const looseText = segments
    .flatMap((segment) => (segment.kind === "context" ? [] : segment.lines))
    .join("\n");
  const looseMax = maxLooseProductIndex(looseText);
  if (looseMax >= 0) {
    entries.push({ model: defaultModel, maxProducts: looseMax + 1 });
  }

  return entries;
}

function isScaffoldLine(line: string): boolean {
  return (
    parseProductContextOpen(line) !== null ||
    isProductContextClose(line) ||
    parseProductBlockOpen(line) !== null ||
    isProductBlockClose(line)
  );
}

/**
 * Body text as the recipient gets it, minus merge-tag resolution, for
 * character counting: the whole-line scaffold goes, and the discover mirrors
 * (sets 2–5, not in the authoring text) come in.
 */
export function stripProductScaffoldLines(text: string): string {
  const own = text.split("\n").filter((line) => !isScaffoldLine(line));
  const segments = parseProductSegments(text);
  const mirrored = expandDiscoverMirrors(segments)
    .filter((segment) => !segments.includes(segment))
    .flatMap((segment) =>
      segment.kind === "context"
        ? segment.children.flatMap((child) => child.lines)
        : [],
    );
  return [...own, ...mirrored].join("\n");
}

/** Re-exported so the editor nodes have one import for both marker pairs. */
export { PRODUCT_BLOCK_CLOSE_MARKER, productBlockOpenMarker };
